import { computed, Injectable, signal } from "@angular/core";
import { EventFormStore } from "../../_services/event-form-store.service";
import { StreappEventStep } from "../../_models/streapp-event-step.model";
import moment from "moment";
import { StreappEvent } from "src/app/models/streappevent.model";

@Injectable()
export class EditEventFormStore extends EventFormStore {

    public stepTrackers = [
        this.stepMediaItemsUploadValid,
        this.stepLocationValid,
        this.stepDetailsValid,
        this.stepPrivacyValid,
        this.stepPriceValid,
    ]

    public steps = computed<Array<StreappEventStep>>(() => {
        return [
            { title: 'step_media_upload', state: this.currentStep() === 0 ? 'active' : (this.stepMediaItemsUploadValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_location', state: this.currentStep() === 1 ? 'active' : (this.stepLocationValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_details', state: this.currentStep() === 2 ? 'active' : (this.stepDetailsValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_privacy', state: this.currentStep() === 3 ? 'active' : (this.stepPrivacyValid() ? 'completed' : 'default'), disabled: false },
            { title: 'step_price', state: this.currentStep() === 4 ? 'active' : (this.stepPriceValid() ? 'completed' : 'default'), disabled: this.uploadItems().length === 0 },
            { title: 'step_publish', state: this.currentStep() === 5 ? 'active' : 'default', disabled: false }
        ];
    });
    
    public readonly canGoNext = computed(() => {
        if (this.isLoading() || this.isPublishing()) {
            return false;
        }

        return this.isStepCompleted(this.currentStep())
    });

    public readonly isValid = computed(() => {
        return this.stepTrackers.every(st => st());
    });

    public async setStep(toStep: number, viaMenu: boolean = false): Promise<void> {
        // Fix real is publishing state check
        if (this.isLoading() || this.isPublishing()) {
            // Prevent navigating while publishing
            return;
        }

        if(this.currentStep() === 3 && toStep !== 3 && this.privacyStepLeaveGuard) {
            const canLeave = await this.privacyStepLeaveGuard();
            if (!canLeave) {
                return;
            }
        }

        // Only trigger to non-disabled step via next/previous buttons
        const stepInList = this.steps()[toStep];
        if(viaMenu && stepInList.disabled) {
            return;
        }

        if(!viaMenu && stepInList.disabled) {
            const forwardStep = toStep > this.currentStep();
            
            const stepsInDirection = forwardStep 
                ? this.steps().slice(toStep) 
                : this.steps().slice(0, toStep).reverse();
            
            const nextNotDisabledStep = stepsInDirection.find(s => !s.disabled);
            const nextNotDisabledStepCount = 
                nextNotDisabledStep 
                    ? this.steps().findIndex(s => s.title === nextNotDisabledStep.title)
                    : undefined;

            if(nextNotDisabledStepCount === undefined) {
                return;
            }

            toStep = nextNotDisabledStepCount;
        }
        
        //Prevent clicking on steps that are not completed (except the last available unfinished step)
        if (toStep > 0 && 
            !this.isStepCompleted(toStep) && 
            !this.isStepCompleted(toStep - 1)
        ) {
            return;
        }

        // Price step is completed by just visiting the page
        if (toStep === 4 && !this.steps()[4].disabled) {
            this.stepPriceValid.set(true);
        }

        this.currentStep.set(toStep);
    }

    public reset(): void {
        this.resetEvent();

        this.currentStep.set(0);
        this.stepTrackers.map(t => t.set(false));

        this.automaticBlur.set(true);
        this.privacyStepLeaveGuard = null;
        
        this.isLoading.set(false);
        this.error.set(null);
        this.eventID.set(undefined);
    }

    private isStepCompleted(index: number): boolean {
        const event = this.streappEvent();
        
        switch (index) {
            case 0:
                // Not required for updating event
                return true;
            case 1:
                return event.latitude !== 0 && event.longtitude !== 0;
            case 2:
                return !!(event.title && event.description);
            case 3:
                return this.stepPrivacyValid() || this.uploadItems().length === 0;
            case 4:
                return this.stepPriceValid() || this.uploadItems().length === 0;
            default:
                return false;
        }
    }

    public async getAndSetEvent(id: string): Promise<StreappEvent | null> {
        try {
            const event = await this.streappService.getEventById(id, this.authService.authorizationHeaderValue);
            if(!event) {
                return null;
            }

            return event;
        } catch (ex: any) {
            console.error(ex);
            return null;
        }
    }

    public initEventData(event: StreappEvent) {
        const category = this.streappService.eventTypes().find(e => e.title === event.category);
        const subcategory = category?.children?.find(e => e.title === event.subcategory);

        this.streappEvent.set({
            id: event.partitionKey,
            title: event.title,
            description: event.description,
            category: category,
            subcategory: subcategory,
            privacyEnabled: true,
            injuriesPresent: event.injuriesPresent,
            emergencyServicesPresent: event.emergencyServicesPresent,
            highImpactEvent: event.isHighImpactEvent,
            tags: event.tags.split(','),
            location: event.locationFullName,
            latitude: event.latitude,
            longtitude: event.longtitude,
            linkedToMainStreappID: event.linkedToMainStreappID,
            created: event.created,
            mediaItems: event.mediaItems,
            preUploadedMediaItems: [],
            preUploadedShopMediaItems: []
        });

        // Trigger completed steps for each step
        this.stepTrackers.map((st, index) => st.set(this.isStepCompleted(index)));
    }

    public setEventData(): any {
        const created = moment(this.streappEvent().created)
            .utcOffset(new Date().getTimezoneOffset())
            .toDate();
        return {
            ID: this.streappEvent().id,
            Title: this.streappEvent().title,
            Description: this.streappEvent().description,
            Category: this.streappEvent().category?.title,
            Subcategory: this.streappEvent().subcategory?.title,
            InjuriesPresent: this.streappEvent().injuriesPresent ?? 'onbekend',
            EmergencyServicesPresent: this.streappEvent().emergencyServicesPresent ?? false,
            ShockingEvent: this.streappEvent().highImpactEvent,
            Tags: this.streappEvent().tags.join(','),
            Latitude: this.streappEvent().latitude,
            Longtitude: this.streappEvent().longtitude,
            LinkedToMainStreappID: this.streappEvent().linkedToMainStreappID,
            CreatedOn: created,
        };
    }

    public async updateEventDetails(): Promise<void> {
        try {
            const eventData = this.setEventData();
            await this.streappService.updateEvent(
                this.streappEvent().id,
                eventData,
                this.authService.authorizationHeaderValue
            );
        } catch (error) {
            this.error.set(this.getErrorMessage(error));
            throw error;
        }
    }

    public async updateEventMediaItems() {
        try {
            await Promise.all([
                this.uploadService.uploadFiles(this.streappEvent().id, this.preUploadedMediaItems(), false, this.streappEvent().privacyEnabled, (index, patch) => this.updateProgress(index, patch.progress!, patch.finished!, false)),
                this.uploadService.uploadFiles(this.streappEvent().id, this.preUploadedShopMediaItems(), true, this.streappEvent().privacyEnabled, (index, patch) => this.updateProgress(index, patch.progress!, patch.finished!, true)),
            ]);
        } catch (error) {
            this.error.set(this.getErrorMessage(error));
            throw error;
        }
    }
}